import type {
  TLMSAdminEditableField,
  TLMSAdminRole,
  TLMSRoleOption,
} from './types'

export const CERTIFICATE_AUTHORITY_ROLE: TLMSAdminRole = 'otorisasi-sertifikat'

export const ROLE_OPTIONS: TLMSRoleOption[] = [
  {
    label: 'super-admin',
    value: 'super-admin',
  },
  {
    label: 'admin-entitas',
    value: 'admin-entitas',
  },
  {
    label: 'admin-pusat',
    value: 'admin-pusat',
  },
  {
    label: 'admin-lptk',
    value: 'admin-lptk',
  },
  {
    label: CERTIFICATE_AUTHORITY_ROLE,
    value: CERTIFICATE_AUTHORITY_ROLE,
  },
]

/**
 * Each property answers a different ACL question:
 * - editableTargetRolesByField: which existing admin roles have fields that
 *   the actor may edit. The target role is always the record's original role.
 * - deletableTargetRoles: which existing admin roles the actor may delete.
 * - assignableRoleValues: which new role values may appear in the create or
 *   update form. These values are not permissions to open the update modal.
 *
 * Omitted properties deny the corresponding capability by default.
 */
type TLMSAdminRolePolicy = {
  editableTargetRolesByField?: Partial<
    Record<TLMSAdminEditableField, readonly TLMSAdminRole[]>
  >
  deletableTargetRoles?: readonly TLMSAdminRole[]
  assignableRoleValues?: Partial<
    Record<'create' | 'update', readonly TLMSAdminRole[]>
  >
}

/** Empty fallback used when an actor has no roles for a capability. */
const NO_ROLES: readonly TLMSAdminRole[] = []

/**
 * Existing admin records whose role a super-admin may edit.
 * `super-admin` is intentionally absent, so existing super-admin rows cannot
 * be edited.
 */
const SUPER_ADMIN_ROLE_EDITABLE_TARGET_ROLES: readonly TLMSAdminRole[] = [
  'admin-pusat',
  'admin-entitas',
  'admin-lptk',
  'instruktur-webinar-pgp',
  CERTIFICATE_AUTHORITY_ROLE,
]

/** Existing admin records whose name a super-admin may edit. */
const SUPER_ADMIN_NAME_EDITABLE_TARGET_ROLES: readonly TLMSAdminRole[] = [
  CERTIFICATE_AUTHORITY_ROLE,
]

/** Existing admin roles a super-admin may delete. */
const SUPER_ADMIN_DELETABLE_TARGET_ROLES: readonly TLMSAdminRole[] = [
  'admin-pusat',
  'admin-entitas',
  'admin-lptk',
  'instruktur-webinar-pgp',
  CERTIFICATE_AUTHORITY_ROLE,
]

/**
 * Role values a super-admin may select when creating or updating an admin.
 * This includes `super-admin`, allowing another admin to be promoted even
 * though an existing super-admin record is not editable.
 */
const SUPER_ADMIN_ASSIGNABLE_ROLE_VALUES: readonly TLMSAdminRole[] = [
  'admin-pusat',
  'admin-entitas',
  'super-admin',
  'admin-lptk',
  'instruktur-webinar-pgp',
  CERTIFICATE_AUTHORITY_ROLE,
]

/**
 * Role values admin-pusat may select when creating or updating an admin.
 */
const ADMIN_PUSAT_ASSIGNABLE_ROLE_VALUES: readonly TLMSAdminRole[] = [
  'admin-entitas',
  'admin-lptk',
  'instruktur-webinar-pgp',
]

/**
 * Existing admin records whose name an admin-pusat may edit.
 */
const ADMIN_PUSAT_NAME_EDITABLE_TARGET_ROLES: readonly TLMSAdminRole[] = [
  CERTIFICATE_AUTHORITY_ROLE,
]

/** Existing admin roles admin-pusat may delete. */
const ADMIN_PUSAT_DELETABLE_TARGET_ROLES: readonly TLMSAdminRole[] = [
  'admin-entitas',
  'admin-lptk',
  'instruktur-webinar-pgp',
]

/** Role values admin-entitas may select in an admin form. */
const ADMIN_ENTITAS_ASSIGNABLE_ROLE_VALUES: readonly TLMSAdminRole[] = [
  'instruktur-webinar-pgp',
]

/**
 * ACL policy keyed by the logged-in actor's role. Empty policy objects mean
 * the actor has no admin-management capabilities.
 */
const ADMIN_ROLE_POLICY: Record<TLMSAdminRole, TLMSAdminRolePolicy> = {
  'super-admin': {
    editableTargetRolesByField: {
      name: SUPER_ADMIN_NAME_EDITABLE_TARGET_ROLES,
      role: SUPER_ADMIN_ROLE_EDITABLE_TARGET_ROLES,
    },
    deletableTargetRoles: SUPER_ADMIN_DELETABLE_TARGET_ROLES,
    assignableRoleValues: {
      create: SUPER_ADMIN_ASSIGNABLE_ROLE_VALUES,
      update: SUPER_ADMIN_ASSIGNABLE_ROLE_VALUES,
    },
  },
  'admin-pusat': {
    editableTargetRolesByField: {
      name: ADMIN_PUSAT_NAME_EDITABLE_TARGET_ROLES,
    },
    deletableTargetRoles: ADMIN_PUSAT_DELETABLE_TARGET_ROLES,
    assignableRoleValues: {
      create: ADMIN_PUSAT_ASSIGNABLE_ROLE_VALUES,
      update: ADMIN_PUSAT_ASSIGNABLE_ROLE_VALUES,
    },
  },
  'admin-entitas': {
    assignableRoleValues: {
      create: ADMIN_ENTITAS_ASSIGNABLE_ROLE_VALUES,
      update: ADMIN_ENTITAS_ASSIGNABLE_ROLE_VALUES,
    },
  },
  'admin-lptk': {},
  'instruktur-webinar-pgp': {},
  'otorisasi-sertifikat': {},
}

const EDITABLE_ADMIN_FIELDS: readonly TLMSAdminEditableField[] = [
  'name',
  'role',
]

export const getEditableAdminFields = ({
  actorRole,
  targetRole,
}: {
  actorRole?: TLMSAdminRole | ''
  targetRole: TLMSAdminRole
}): TLMSAdminEditableField[] => {
  if (!actorRole) return []

  const { editableTargetRolesByField } = ADMIN_ROLE_POLICY[actorRole]

  return EDITABLE_ADMIN_FIELDS.filter((field) =>
    editableTargetRolesByField?.[field]?.includes(targetRole),
  )
}

export const canDeleteAdmin = ({
  actorRole,
  targetRole,
}: {
  actorRole?: TLMSAdminRole | ''
  targetRole: TLMSAdminRole
}) => {
  if (!actorRole) return false

  return (
    ADMIN_ROLE_POLICY[actorRole].deletableTargetRoles?.includes(targetRole) ??
    false
  )
}

export const getAssignableAdminRoleOptions = ({
  actorRole,
  mode,
  roleOptions,
}: {
  actorRole?: TLMSAdminRole | ''
  mode: 'create' | 'update'
  roleOptions: TLMSRoleOption[]
}) => {
  if (!actorRole) return []

  const availableRoles =
    ADMIN_ROLE_POLICY[actorRole].assignableRoleValues?.[mode] ?? NO_ROLES

  return roleOptions.filter((role) => availableRoles.includes(role.value))
}
